/**
 * Страницы очереди домашек (28.09.2026, разбор удобства админки №3): раньше очередь показывала первые 20 из 2172
 * сдач и не листалась. Теперь — по 50 с «Показать ещё».
 */
export const HOMEWORK_PAGE = 50;

type QueuePage<T> = { items: T[]; total: number };

/**
 * Откуда грузить следующую страницу; `undefined` — всё загружено. Пустая последняя страница тоже конец: очередь
 * убывает, пока её разбирают, и `total` первой страницы может оказаться больше, чем осталось.
 */
export function nextQueueOffset<T>(pages: readonly QueuePage<T>[]): number | undefined {
  const last = pages[pages.length - 1];
  if (!last || last.items.length === 0) return undefined;
  const loaded = pages.reduce((n, p) => n + p.items.length, 0);
  return loaded < last.total ? loaded : undefined;
}

/**
 * Все загруженные строки подряд, без повторов: пока куратор листает, проверенные работы уходят из очереди, и
 * строка может сдвинуться со страницы на следующую — в списке она была бы дважды.
 */
export function flattenQueue<T extends { id: string }>(pages: readonly QueuePage<T>[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const p of pages) {
    for (const it of p.items) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      out.push(it);
    }
  }
  return out;
}

/**
 * Какую работу открыть после решения в окне проверки (решение владельца 13.10.2026, docs/CURATOR-MOBILE-DESIGN.md
 * §10.2): сразу следующую из той же вкладки очереди, а не закрывать окно и не возвращать в список. Кончилась —
 * `null`, окно закрывается.
 *
 * Следующая — идущая за текущей; за ней ничего нет — первая из оставшихся выше (куратор мог начать не с начала).
 * Решённые в этом заходе пропускаются: список перечитывается не мгновенно, а во вкладках «Принятые» и прочих
 * решённая работа и вовсе остаётся — без пропуска окно ходило бы по кругу. Текущей работы в списке может не быть
 * (открыта по ссылке из уведомления) — тогда первая не решённая.
 */
export function nextInQueue<T extends { id: string }>(
  items: readonly T[],
  currentId: string,
  doneIds: ReadonlySet<string>,
): T | null {
  const open = (it: T) => it.id !== currentId && !doneIds.has(it.id);
  const at = items.findIndex((it) => it.id === currentId);
  return items.slice(at + 1).find(open) ?? items.slice(0, Math.max(at, 0)).find(open) ?? null;
}
